import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import {
    useGetNirovisionAccessRequestEventImageQuery,
    useGetNirovisionAccessRequestFaceImageQuery,
    useGetNirovisionAccessRequestFacesQuery,
    useGetNirovisionAccessRequestUsersQuery,
    useGetNirovisionAccessRequestsQuery,
    useRespondToNirovisionAccessRequestMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import {
    NirovisionAccessMode,
    NirovisionAccessRequest,
    NirovisionAccessRequestStatus,
    NirovisionAccessRequestUser,
} from '../../../app/Slices/SevenDayApiTypes';
import CustomAutocomplete from '../../../layout/styledcomponents/CustomAutoComplete';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { theme } from '../../../shared/theme';
import { handleHttpError } from '../../../shared/utils';
import toast from '../../shared/Toast';
import ProfilePicture from '../Profile/ProfilePicture';
import { isValidEmail } from '../Reservations/ReservationRequest/FormSections/ReservationRequestSections/AttendeesSection';
import { splitIdentityName } from './nirovisionAccessRequestUtils';

type Props = {
    bookingId: number;
    reservationStatus?: string;
    actionsEnabled: boolean;
};

type ProfilePictureSelection =
    | { source: 'face'; faceId: string }
    | { source: 'event' };

type UserSelection =
    | { source: 'user'; user: NirovisionAccessRequestUser }
    | { source: 'email'; email: string };

type UserOption =
    | { source: 'user'; user: NirovisionAccessRequestUser; label: string }
    | { source: 'email'; email: string; label: string };

const filterUserOptions = createFilterOptions<UserOption>();

const requestColumns =
    '136px minmax(0, 1.4fr) minmax(8.5rem, 0.7fr) minmax(6.5rem, 0.5fr) minmax(16rem, 0.8fr)';

const STATUS_LABELS: Record<NirovisionAccessRequestStatus, string> = {
    pending: 'Pending',
    approved: 'Approved',
    denied: 'Denied',
};

const headerTypographySx = {
    fontWeight: 700,
    letterSpacing: 0.2,
};

const RequestHeaderCell = ({
    label,
    align,
}: {
    label: string;
    align?: 'left' | 'center';
}) => (
    <Typography
        component='h6'
        variant='body3'
        sx={{
            ...headerTypographySx,
            width: '100%',
            px: 1,
            textAlign: align || 'left',
        }}
    >
        {label}
    </Typography>
);

const ACCESS_MODE_OPTIONS: {
    value: NirovisionAccessMode;
    label: string;
    description: string;
}[] = [
    {
        value: 'reservation',
        label: 'Add to reservation',
        description:
            'They will be added as a guest and granted full face recognition access for this reservation.',
    },
    {
        value: 'one_time',
        label: 'One-time check-in',
        description:
            'They will get temporary door access until they enter. They will not be added as a guest.',
    },
    {
        value: 'day_pass',
        label: 'Day pass',
        description:
            'They will get temporary door access until the end of the day. They will not be added as a guest.',
    },
];

const toUserOption = (user: NirovisionAccessRequestUser): UserOption => ({
    source: 'user',
    user,
    label: user.firstName
        ? `${user.firstName} ${user.lastName} (${user.email})`.trim()
        : user.email,
});

const ImageOption = ({
    src,
    alt,
    selected,
    isLoading,
    onSelect,
}: {
    src?: string;
    alt: string;
    selected: boolean;
    isLoading?: boolean;
    onSelect: () => void;
}) => (
    <Button
        onClick={onSelect}
        sx={{
            p: 0.5,
            minWidth: 0,
            border: selected ? '2px solid' : '1px solid',
            borderColor: selected ? 'primary.main' : 'divider',
            borderRadius: 1,
        }}
    >
        {isLoading ? (
            <Box
                sx={{
                    width: 72,
                    height: 72,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <CircularProgress size={20} />
            </Box>
        ) : src ? (
            <Box
                component='img'
                src={src}
                alt={alt}
                sx={{ width: 72, height: 72, objectFit: 'cover' }}
            />
        ) : (
            <Box
                sx={{
                    width: 72,
                    height: 72,
                    bgcolor: 'action.hover',
                }}
            />
        )}
    </Button>
);

const FaceOption = ({
    requestId,
    faceId,
    selected,
    onSelect,
}: {
    requestId: number;
    faceId: string;
    selected: boolean;
    onSelect: (faceId: string) => void;
}) => {
    const { data: src, isLoading } =
        useGetNirovisionAccessRequestFaceImageQuery({ id: requestId, faceId });

    return (
        <ImageOption
            src={src}
            alt='Nirovision face'
            selected={selected}
            isLoading={isLoading}
            onSelect={() => onSelect(faceId)}
        />
    );
};

const AccessRequestRow = ({
    request,
    isMobile,
    reservationStatus,
    actionsEnabled,
}: {
    request: NirovisionAccessRequest;
    isMobile: boolean;
    reservationStatus?: string;
    actionsEnabled: boolean;
}) => {
    const [decision, setDecision] = useState<'' | 'approve' | 'deny'>('');
    const [accessMode, setAccessMode] = useState<NirovisionAccessMode | ''>('');
    const [selectedPicture, setSelectedPicture] =
        useState<ProfilePictureSelection>();
    const [selectedUser, setSelectedUser] = useState<UserSelection>();
    const [userInput, setUserInput] = useState('');
    const [userError, setUserError] = useState('');
    const [pictureError, setPictureError] = useState('');
    const [respond, { isLoading }] =
        useRespondToNirovisionAccessRequestMutation();
    const { data: candidateUsers = [], isFetching: isUsersLoading } =
        useGetNirovisionAccessRequestUsersQuery(request.id, {
            skip: accessMode !== 'reservation' || !request.needsUser,
        });
    const selectedHasPhoto =
        selectedUser?.source === 'user' && selectedUser.user.hasPhoto;
    const awaitingIdentity = request.needsUser && !selectedUser;
    const { data: faces = [], isLoading: isFacesLoading } =
        useGetNirovisionAccessRequestFacesQuery(request.id, {
            skip:
                accessMode !== 'reservation' ||
                !request.needsProfilePicture ||
                selectedHasPhoto,
        });
    const { data: eventImageSrc, isError: isEventImageError } =
        useGetNirovisionAccessRequestEventImageQuery(request.id, {
            skip: !request.hasEventImage,
        });

    const { firstName, lastName } = splitIdentityName(request.identityName);
    const statusLabel = STATUS_LABELS[request.status];
    const actionsAvailable =
        request.status === 'pending' && reservationStatus === 'approved';
    const canRespond = actionsEnabled && actionsAvailable;
    const snapshot =
        request.hasEventImage && eventImageSrc && !isEventImageError ? (
            <Box
                component='img'
                src={eventImageSrc}
                alt='Camera snapshot'
                sx={{
                    width: 120,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 1,
                    flexShrink: 0,
                }}
            />
        ) : null;
    const canUseEventImage = request.hasEventImage && !isEventImageError;
    const hasPictureOptions = faces.length > 0 || canUseEventImage;
    const awaitingFaceOptions =
        accessMode === 'reservation' &&
        request.needsProfilePicture &&
        !selectedHasPhoto &&
        !awaitingIdentity &&
        isFacesLoading;
    const needsPicture =
        accessMode === 'reservation' &&
        request.needsProfilePicture &&
        !selectedHasPhoto &&
        !awaitingIdentity &&
        (hasPictureOptions || awaitingFaceOptions);
    const userOptions = useMemo(
        () => candidateUsers.map(toUserOption),
        [candidateUsers],
    );
    const selectedUserOption = selectedUser
        ? selectedUser.source === 'user'
            ? toUserOption(selectedUser.user)
            : {
                  source: 'email' as const,
                  email: selectedUser.email,
                  label: `Create new guest (${selectedUser.email})`,
              }
        : null;

    const handleRespond = async (action: 'approve' | 'deny') => {
        if (
            action === 'approve' &&
            accessMode === 'reservation' &&
            request.needsUser &&
            !selectedUser
        ) {
            setUserError('Choose an existing user or enter an email address.');
            return;
        }

        if (action === 'approve' && needsPicture && !selectedPicture) {
            setPictureError('Choose a 7DAY profile picture before approving.');
            return;
        }

        const reservationFields =
            accessMode === 'reservation'
                ? {
                      ...(selectedUser?.source === 'user'
                          ? { userId: selectedUser.user.id }
                          : selectedUser?.source === 'email'
                            ? { email: selectedUser.email }
                            : {}),
                      ...(selectedPicture?.source === 'face'
                          ? { profilePictureFaceId: selectedPicture.faceId }
                          : selectedPicture?.source === 'event'
                            ? { useEventImage: true }
                            : {}),
                  }
                : {};

        try {
            await respond({
                id: request.id,
                action,
                ...(action === 'approve' && accessMode
                    ? {
                          accessMode,
                          ...reservationFields,
                      }
                    : {}),
            }).unwrap();
            toast.success(
                action === 'approve'
                    ? accessMode === 'reservation'
                        ? 'Access approved and guest added to the reservation.'
                        : accessMode === 'day_pass'
                          ? 'Day pass approved.'
                          : 'One-time check-in approved.'
                    : 'Access request denied.',
            );
        } catch (error) {
            toast.error(handleHttpError(error));
        }
    };

    return (
        <Box
            sx={
                isMobile
                    ? {
                          p: 1.25,
                          backgroundColor: theme.palette.neutral.main,
                          border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                          borderRadius: 2,
                          boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                      }
                    : {
                          display: 'flex',
                          flexDirection: 'column',
                          borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                          '&:hover': {
                              backgroundColor:
                                  theme.palette.custom.cardActionBackground,
                          },
                      }
            }
        >
            <Box
                sx={
                    isMobile
                        ? {
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              columnGap: 2,
                              rowGap: 1.5,
                          }
                        : {
                              display: 'grid',
                              gridTemplateColumns: requestColumns,
                              alignItems: 'center',
                          }
                }
            >
                {isMobile ? (
                    snapshot
                ) : (
                    <Box
                        sx={{
                            padding: '10px 8px',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        {snapshot}
                    </Box>
                )}

                <Box
                    sx={
                        isMobile
                            ? {
                                  display: 'flex',
                                  gap: 1.5,
                                  alignItems: 'flex-start',
                                  flex: '1 1 20rem',
                                  minWidth: 'min(100%, 20rem)',
                              }
                            : {
                                  display: 'flex',
                                  gap: 1,
                                  alignItems: 'center',
                                  minWidth: 0,
                                  padding: '10px 8px',
                              }
                    }
                >
                    {request.needsProfilePicture ? (
                        <ProfilePicture
                            profileDetails={{
                                firstName,
                                lastName,
                                email: request.identityEmail,
                            }}
                            src={request.profilePicture}
                            sx={{
                                width: isMobile ? 42 : 40,
                                height: isMobile ? 42 : 40,
                                flexShrink: 0,
                            }}
                        />
                    ) : null}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            sx={{
                                fontWeight: isMobile ? 950 : 800,
                                fontSize: isMobile ? 14 : 13,
                                lineHeight: isMobile ? 1.15 : undefined,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {request.identityName || 'Unknown guest'}
                        </Typography>
                        <Typography
                            sx={{
                                mt: isMobile ? 0.25 : 0,
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {request.identityEmail || 'No email provided'}
                        </Typography>
                        {isMobile ? (
                            <>
                                <Typography
                                    sx={{
                                        mt: 0.25,
                                        fontSize: 12,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    Requested{' '}
                                    {moment(request.createdAt).format(
                                        'D MMM YYYY, h:mm A',
                                    )}
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 0.25,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {statusLabel}
                                </Typography>
                            </>
                        ) : null}
                    </Box>
                </Box>

                {isMobile ? null : (
                    <Typography
                        sx={{
                            padding: '10px 8px',
                            fontSize: 13,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        {moment(request.createdAt).format('D MMM YYYY, h:mm A')}
                    </Typography>
                )}

                {isMobile ? null : (
                    <Typography
                        sx={{
                            padding: '10px 8px',
                            fontSize: 13,
                            fontWeight: 700,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        {statusLabel}
                    </Typography>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: decision === 'deny' ? 'column' : 'row',
                        flexWrap: 'wrap',
                        gap: decision === 'deny' ? 1 : 1.5,
                        alignItems: isMobile ? 'stretch' : 'center',
                        justifyContent: isMobile ? 'flex-start' : 'center',
                        ...(isMobile
                            ? { width: '100%' }
                            : { padding: '10px 8px' }),
                        '& .MuiButton-root': {
                            borderRadius: 2,
                            textTransform: 'none',
                            height: 34,
                            minWidth: isMobile ? 0 : 96,
                            flex: isMobile ? 1 : undefined,
                            px: 1.5,
                        },
                    }}
                >
                    {actionsAvailable && !canRespond ? (
                        <>
                            <CustomButton
                                variant='outlined'
                                color='warning'
                                disabled
                            >
                                Deny
                            </CustomButton>
                            <CustomButton
                                variant='outlined'
                                color='primary'
                                disabled
                            >
                                Accept
                            </CustomButton>
                        </>
                    ) : null}

                    {canRespond && decision === '' ? (
                        <>
                            <CustomButton
                                variant='outlined'
                                color='warning'
                                disabled={isLoading}
                                onClick={() => setDecision('deny')}
                            >
                                Deny
                            </CustomButton>
                            <CustomButton
                                variant='outlined'
                                color='primary'
                                disabled={isLoading}
                                onClick={() => setDecision('approve')}
                            >
                                Accept
                            </CustomButton>
                        </>
                    ) : null}

                    {canRespond && decision === 'deny' ? (
                        <>
                            <Typography
                                sx={{
                                    width: '100%',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    lineHeight: 1.35,
                                    textAlign: isMobile ? 'left' : 'center',
                                }}
                            >
                                Are you sure you want to deny this access
                                request?
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                    alignItems: 'center',
                                    width: isMobile ? '100%' : 'auto',
                                }}
                            >
                                <CustomButton
                                    variant='outlined'
                                    color='warning'
                                    loading={isLoading}
                                    disabled={isLoading}
                                    onClick={() => handleRespond('deny')}
                                >
                                    Deny
                                </CustomButton>
                                <CustomButton
                                    variant='outlined'
                                    disabled={isLoading}
                                    onClick={() => setDecision('')}
                                >
                                    Cancel
                                </CustomButton>
                            </Box>
                        </>
                    ) : null}

                    {canRespond && decision === 'approve' ? (
                        <>
                            <CustomButton
                                variant='contained'
                                color='primary'
                                loading={isLoading}
                                disabled={
                                    isLoading ||
                                    !accessMode ||
                                    awaitingFaceOptions
                                }
                                onClick={() => handleRespond('approve')}
                            >
                                Accept
                            </CustomButton>
                            <CustomButton
                                variant='outlined'
                                disabled={isLoading}
                                onClick={() => {
                                    setDecision('');
                                    setAccessMode('');
                                }}
                            >
                                Cancel
                            </CustomButton>
                        </>
                    ) : null}
                </Box>
            </Box>

            {canRespond && decision === 'approve' ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2.5,
                        px: isMobile ? 0 : 1,
                        pt: isMobile ? 0 : 0.5,
                        pb: isMobile ? 0 : 1.5,
                    }}
                >
                    <FormControl disabled={isLoading}>
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 700, mb: 1 }}
                        >
                            Choose access mode
                        </Typography>
                        <RadioGroup
                            value={accessMode}
                            onChange={(event) => {
                                setAccessMode(
                                    event.target.value as NirovisionAccessMode,
                                );
                                setUserError('');
                                setPictureError('');
                            }}
                        >
                            {ACCESS_MODE_OPTIONS.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    sx={{ alignItems: 'flex-start' }}
                                    control={
                                        <Radio size='small' sx={{ py: 0.25 }} />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant='body2'>
                                                {option.label}
                                            </Typography>
                                            {accessMode === option.value ? (
                                                <Typography
                                                    variant='body2'
                                                    color='text.secondary'
                                                >
                                                    {option.description}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    {accessMode === 'reservation' && request.needsUser ? (
                        <Box>
                            <Typography
                                variant='body2'
                                sx={{ fontWeight: 700, mb: 1 }}
                            >
                                Identify this person
                            </Typography>
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ mb: 1 }}
                            >
                                Choose an existing 7DAY user or enter an email
                                for a new guest.
                            </Typography>
                            {isUsersLoading ? (
                                <CircularProgress size={20} />
                            ) : (
                                <CustomAutocomplete<
                                    UserOption,
                                    false,
                                    false,
                                    true
                                >
                                    freeSolo
                                    options={userOptions}
                                    value={selectedUserOption}
                                    inputValue={userInput}
                                    onInputChange={(_, value) =>
                                        setUserInput(value)
                                    }
                                    getOptionLabel={(option) =>
                                        typeof option === 'string'
                                            ? option
                                            : option.label
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.source === value.source &&
                                        (option.source === 'user' &&
                                        value.source === 'user'
                                            ? option.user.id === value.user.id
                                            : option.source === 'email' &&
                                                value.source === 'email'
                                              ? option.email === value.email
                                              : false)
                                    }
                                    filterOptions={(options, params) => {
                                        const filtered = filterUserOptions(
                                            options,
                                            params,
                                        );
                                        const input = params.inputValue.trim();
                                        const alreadyListed = options.some(
                                            (option) =>
                                                option.source === 'user' &&
                                                option.user.email.toLowerCase() ===
                                                    input.toLowerCase(),
                                        );
                                        if (
                                            isValidEmail(input) &&
                                            !alreadyListed
                                        ) {
                                            filtered.push({
                                                source: 'email',
                                                email: input.toLowerCase(),
                                                label: `Create new guest (${input})`,
                                            });
                                        }
                                        return filtered;
                                    }}
                                    onChange={(_, value) => {
                                        setSelectedPicture(undefined);
                                        setPictureError('');
                                        if (typeof value === 'string') {
                                            const email = value.trim();
                                            const isEmail = isValidEmail(email);
                                            setSelectedUser(
                                                isEmail
                                                    ? {
                                                          source: 'email',
                                                          email: email.toLowerCase(),
                                                      }
                                                    : undefined,
                                            );
                                            if (isEmail) setUserError('');
                                            return;
                                        }
                                        if (!value) {
                                            setSelectedUser(undefined);
                                            return;
                                        }
                                        setSelectedUser(
                                            value.source === 'user'
                                                ? {
                                                      source: 'user',
                                                      user: value.user,
                                                  }
                                                : {
                                                      source: 'email',
                                                      email: value.email,
                                                  },
                                        );
                                        setUserError('');
                                    }}
                                    renderOption={(props, option) => {
                                        if (typeof option === 'string') {
                                            return <li {...props}>{option}</li>;
                                        }
                                        const profileDetails =
                                            option.source === 'user'
                                                ? {
                                                      firstName:
                                                          option.user.firstName,
                                                      lastName:
                                                          option.user.lastName,
                                                      email: option.user.email,
                                                  }
                                                : {
                                                      firstName: '',
                                                      lastName: '',
                                                      email: option.email,
                                                  };
                                        return (
                                            <li {...props}>
                                                <ProfilePicture
                                                    profileDetails={
                                                        profileDetails
                                                    }
                                                    src={
                                                        option.source === 'user'
                                                            ? option.user
                                                                  .profilePicture
                                                            : undefined
                                                    }
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        fontSize: 11,
                                                        flexShrink: 0,
                                                        mr: 1,
                                                    }}
                                                />
                                                <Box
                                                    component='span'
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {option.label}
                                                </Box>
                                            </li>
                                        );
                                    }}
                                    error={!!userError}
                                    helperText={userError}
                                    placeholder='Search users or enter an email'
                                />
                            )}
                        </Box>
                    ) : null}

                    {accessMode === 'reservation' &&
                    request.needsProfilePicture &&
                    !selectedHasPhoto &&
                    !awaitingIdentity ? (
                        <Box>
                            <Typography
                                variant='body2'
                                sx={{ fontWeight: 700, mb: 1 }}
                            >
                                Choose a 7DAY profile picture
                            </Typography>
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ mb: 1 }}
                            >
                                This person has no profile picture. Choose a
                                photo to use as their profile picture in 7DAY.
                            </Typography>
                            {awaitingFaceOptions ? (
                                <CircularProgress size={20} />
                            ) : hasPictureOptions ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    {faces.map((face) => (
                                        <FaceOption
                                            key={face.id}
                                            requestId={request.id}
                                            faceId={face.id}
                                            selected={
                                                selectedPicture?.source ===
                                                    'face' &&
                                                selectedPicture.faceId ===
                                                    face.id
                                            }
                                            onSelect={(faceId) => {
                                                setSelectedPicture({
                                                    source: 'face',
                                                    faceId,
                                                });
                                                setPictureError('');
                                            }}
                                        />
                                    ))}
                                    {canUseEventImage ? (
                                        <ImageOption
                                            src={eventImageSrc}
                                            alt='Camera snapshot'
                                            selected={
                                                selectedPicture?.source ===
                                                'event'
                                            }
                                            isLoading={!eventImageSrc}
                                            onSelect={() => {
                                                setSelectedPicture({
                                                    source: 'event',
                                                });
                                                setPictureError('');
                                            }}
                                        />
                                    ) : null}
                                </Box>
                            ) : (
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                >
                                    No Nirovision photos are available for this
                                    person.
                                </Typography>
                            )}
                            {pictureError ? (
                                <FormHelperText error>
                                    {pictureError}
                                </FormHelperText>
                            ) : null}
                        </Box>
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
};

export default function NirovisionAccessRequestsTab({
    bookingId,
    reservationStatus,
    actionsEnabled,
}: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const { data: requests = [], isFetching } =
        useGetNirovisionAccessRequestsQuery(undefined, {
            skip: !bookingId,
        });

    const bookingRequests = useMemo(
        () => requests.filter((request) => request.bookingId === bookingId),
        [bookingId, requests],
    );

    if (isFetching && !bookingRequests.length) {
        return (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (!bookingRequests.length) {
        return (
            <Typography color='text.secondary'>
                You have no access requests for this reservation.
            </Typography>
        );
    }

    const rows = bookingRequests.map((request) => (
        <AccessRequestRow
            key={request.id}
            request={request}
            isMobile={isMobile}
            reservationStatus={reservationStatus}
            actionsEnabled={actionsEnabled}
        />
    ));

    if (isMobile) {
        return <Stack spacing={1.25}>{rows}</Stack>;
    }

    return (
        <Paper
            elevation={0}
            square
            sx={{
                backgroundColor: theme.palette.neutral.main,
                p: { xs: 2, sm: 3, md: 3.5 },
                boxShadow: 'none',
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: requestColumns,
                    alignItems: 'center',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    minHeight: 56,
                    backgroundColor: theme.palette.neutral.main,
                    borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                }}
            >
                <RequestHeaderCell label='' />
                <RequestHeaderCell label='Guest' />
                <RequestHeaderCell label='Requested' />
                <RequestHeaderCell label='Status' />
                <RequestHeaderCell label='Actions' align='center' />
            </Box>
            {rows}
        </Paper>
    );
}
